import cx from "classnames";
import camelCase from "lodash-es/camelCase";
import { useCallback, useEffect, useRef } from "react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";

import useAriaModalAttribute from "hooks/useAriaModalAttribute";
import { withTestId } from "utils/withTestId";

import styles from "./styles.module.css";
import { DrawerProps } from "./types";

type DrawerInnerProps = Pick<DrawerProps, "onExit" | "children"> & {
  isExiting: boolean;
};

const DrawerInner = ({ children, isExiting, onExit }: DrawerInnerProps) => {
  const onExitRef = useRef(onExit);

  useEffect(() => {
    // Store the latest onExit callback in a ref to avoid specifying it as a dependency of the effect below.
    // Otherwise the effect would be called every time the onExit callback changes.
    onExitRef.current = onExit;
  }, [onExit]);

  useEffect(() => {
    return () => {
      if (isExiting) {
        onExitRef.current?.();
      }
    };
  }, [isExiting]);

  const dialogRef = useAriaModalAttribute();

  return (
    <Dialog ref={dialogRef} className={styles.drawerInner}>
      {children}
    </Dialog>
  );
};

DrawerInner.displayName = "DS.DrawerInner";

const DrawerNew = ({
  children,
  isOpen,
  onOpen,
  onClose,
  onExit,
  isDismissable = true,
  variant = "normal",
  "data-testid": dataTestId,
  shouldCloseOnInteractOutside,
}: DrawerProps) => {
  const handleOnOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        onOpen?.();
      } else {
        onClose?.();
      }
    },
    [onOpen, onClose]
  );

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={handleOnOpenChange}
      isDismissable={isDismissable}
      shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
    >
      <Modal
        {...withTestId(dataTestId)}
        className={cx(styles.drawer, styles[camelCase(`variant-${variant}`)])}
        data-modal
      >
        {({ isExiting }) => (
          <DrawerInner isExiting={isExiting} onExit={onExit}>
            {children}
          </DrawerInner>
        )}
      </Modal>
    </ModalOverlay>
  );
};

DrawerNew.displayName = "DS.DrawerNew";

export default DrawerNew;
